import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { getCustomerCriteriasQuery } from "rsl-api-106/lib/queries";
import { Query, QueryGetCustomerCriteriasArgs } from "rsl-api-106/lib/types";

import { useQuery } from "@apollo/client";
import InputCheckbox from "@components/_Layout/Controls/InputCheckbox";
import Label from "@components/_Layout/Controls/InputLabel";
import InputText from "@components/_Layout/Controls/InputText";
import Select from "@components/_Layout/Controls/Select";
import { useAuth, useSession } from "@hooks";

type CustomerCriteriasProps = {
  customerCriteriaValueInput: any;
  setCustomerCriteriaValueInput: (criterias: any) => void;
  criteriaCodes?: string[];
};

const languagesVarsCodes = [
  "LANG_neobooking_loading_additional_information_in_progress", //Chargement des informations complémentaires...
];

const CustomerCriterias: React.FC<CustomerCriteriasProps> = ({
  customerCriteriaValueInput,
  setCustomerCriteriaValueInput,
  criteriaCodes,
}) => {
  /* Hooks */
  const { sessionId, getLanguageVarValue } = useSession();
  const { user } = useAuth();
  const { data, loading } = useQuery<Query, QueryGetCustomerCriteriasArgs>(
    getCustomerCriteriasQuery(),
    {
      variables: {
        session: { name: sessionId!! },
        input: {
          criteriaCodes: criteriaCodes,
        },
      },
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (user) {
      data?.getCustomerCriterias?.criterias?.map((criteria) => {
        if (criteria) {
          const userCriterias = user?.criterias?.find(
            (x) => x?.code === criteria.code
          );
          if (userCriterias && userCriterias.value) {
            handleChangeCustomerCriteriaValueInputByName(
              criteria.code,
              userCriterias.value
            );
          }
        }
      });
    } else {
      //si pas de user, on parcours les critères et si ils sont de type isChoice (select), on affecte la 1er value par défaut
      data?.getCustomerCriterias?.criterias?.forEach((criteria) => {
        if (
          criteria &&
          criteria.isChoice &&
          criteria.choices &&
          criteria.choices.length > 0
        ) {
          handleChangeCustomerCriteriaValueInputByName(
            criteria.code,
            criteria.choices[0]?.code ?? ""
          );
        }
      });
    }
  }, [user, data]);

  const handleChangeCriteriasValue = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setCustomerCriteriaValueInput({
      ...customerCriteriaValueInput,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeCriteriasValueCheckBox = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomerCriteriaValueInput({
      ...customerCriteriaValueInput,
      [e.target.name]: e.target.checked ? "1" : "0",
    });
  };

  const handleChangeCustomerCriteriaValueInputByName = (
    name: string,
    value: string
  ) => {
    setCustomerCriteriaValueInput((prev: any) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="tw-flex tw-flex-col">
      {data && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          {data.getCustomerCriterias?.criterias &&
            data.getCustomerCriterias.criterias.length > 0 &&
            data.getCustomerCriterias.criterias.map(
              (customerCriteria, customerCriteriaIndex) => {
                return (
                  <div
                    key={`customerCriterias_${customerCriteriaIndex}`}
                    className="tw-col-span-2"
                  >
                    {customerCriteria?.isFile && (
                      <>
                        <input
                          className="tw-block tw-w-full tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-300 tw-transition tw-ease-in-out tw-m-0 focus:tw-border-primary-500 focus:tw-outline-none"
                          type="file"
                          required={customerCriteria.mandatory ?? undefined}
                          onChange={handleChangeCriteriasValue}
                          name={customerCriteria?.code}
                          value={
                            customerCriteriaValueInput[
                              customerCriteria?.code
                            ] ?? ""
                          }
                          id="formFile"
                        />
                      </>
                    )}
                    {customerCriteria?.isFree && (
                      <InputText
                        required={customerCriteria.mandatory ?? undefined}
                        onChange={handleChangeCriteriasValue}
                        value={
                          customerCriteriaValueInput[customerCriteria?.code] ??
                          ""
                        }
                        name={customerCriteria?.code}
                        additionalClassname="tw-w-full"
                        placeholder={customerCriteria?.label!!}
                      />
                    )}
                    {customerCriteria?.isChoice &&
                      !["NEWS", "SMS"].includes(customerCriteria?.code) && (
                        <Select
                          value={
                            customerCriteriaValueInput[customerCriteria?.code]
                          }
                          onChange={handleChangeCriteriasValue}
                          name={customerCriteria?.code}
                          required={customerCriteria.mandatory ?? undefined}
                          options={customerCriteria.choices?.map((x) => {
                            return { label: x?.label!!, value: x?.code!! };
                          })}
                          additionalClassname="tw-w-full"
                        />
                      )}
                    {customerCriteria?.isChoice &&
                      ["NEWS", "SMS"].includes(customerCriteria?.code) && (
                        <div className="tw-flex tw-items-center tw-gap-x-2">
                          <InputCheckbox
                            onChange={handleChangeCriteriasValueCheckBox}
                            name={customerCriteria?.code}
                            id={`cms_${customerCriteria?.code}`}
                            checked={
                              customerCriteriaValueInput?.[
                                customerCriteria?.code
                              ] === "1"
                            }
                            onLoad={handleChangeCriteriasValueCheckBox}
                            required={
                              customerCriteria?.mandatory
                                ? customerCriteria.mandatory
                                : false
                            }
                          />
                          <Label
                            truncate={false}
                            required={
                              customerCriteria?.mandatory
                                ? customerCriteria.mandatory
                                : false
                            }
                            additionalClassname="!tw-font-normal"
                            htmlFor={`cms_${customerCriteria?.code}`}
                          >
                            {customerCriteria?.label}
                          </Label>
                        </div>
                      )}
                  </div>
                );
              }
            )}
        </div>
      )}
    </div>
  );
};

CustomerCriterias.propTypes = {
  //
};

export default CustomerCriterias;
